import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
  Signal,
  viewChild
} from '@angular/core';
import {
  CardContainerComponent,
  ChatComponent,
  closePopupData,
  createErrorPopupData,
  FilePrintingButtonComponent,
  htmlInjectorValidator,
  IconComponent,
  Message,
  NoValue,
  StatusMessagePopupV2Component,
  StatusPopupElement,
  TextAreaSignalInputComponent,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface
} from '@tribeauto/ngx-mobilite-material';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ChatForm } from './messaging.model';
import { MessagingService } from './messaging.service';
import { ActivatedRoute } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { AsyncPipe, DatePipe } from '@angular/common';
import { catchError, EMPTY, filter, Observable, of, switchMap, tap } from 'rxjs';
import { DocumentsService } from '../documents/documents.service';
import { ApplicationStore } from '../../../../core/services/application-store.service';
import { DocumentHistoryResponse } from '../documents/documents-model';
import { arrayBufferConverterToFile } from '../../../helpers/file-conversion.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField, maxLength } from '@angular/forms/signals';
import { AUTO_CLOSE_DELAY_TEN_SECONDS } from '../../../helpers/popup.helper';

@Component({
  selector: 'pvmo-messaging',
  imports: [
    CardContainerComponent,
    IconComponent,
    ReactiveFormsModule,
    ChatComponent,
    MatButton,
    FilePrintingButtonComponent,
    DatePipe,
    AsyncPipe,
    TranslatePipe,
    FormField,
    StatusMessagePopupV2Component,
    TextAreaSignalInputComponent
  ],
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    }
  ],
  templateUrl: './messaging.component.html',
  styleUrl: './messaging.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessagingComponent implements OnInit, AfterViewChecked {
  private readonly locale = inject(LOCALE_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly messagingService = inject(MessagingService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly documentsService = inject(DocumentsService);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly chatModel = signal<ChatForm>({ message: '' });

  readonly documentsContainer: Signal<ElementRef> = viewChild.required('documentsContainer');
  readonly chatForm = form(this.chatModel, path => {
    maxLength(path.message, 1000);
    htmlInjectorValidator(path.message);
  });

  messageList: Signal<Message[]> = computed(() => this.messagingService.lastMessageList());
  chatMessageResponseListError = computed(() => this.messagingService.lastChatMessageResponseListError());
  id = this.activatedRoute.parent?.snapshot.paramMap.get('id');
  dateFormat = new Intl.DateTimeFormat(this.locale, { dateStyle: 'short' })
    .formatToParts(new Date())
    .map(p => {
      switch (p.type) {
        case 'day':
          return 'dd';
        case 'month':
          return 'MM';
        case 'year':
          return 'yyyy';
        default:
          return p.value;
      }
    })
    .join('');
  documentHistory$: Observable<DocumentHistoryResponse[]> | undefined;
  documentsList: DocumentHistoryResponse[] = [];
  documentLoadingIds = new Map<string, boolean>();
  scrollHeightInitialized = false;
  isDocumentDownloadingErrorPopup = false;
  downloadFileStatusPopupElement: StatusPopupElement = closePopupData(
    createErrorPopupData(
      this.translateService.instant('application.chat.download-error-title'),
      this.translateService.instant('application.chat.download-error-message'),
      this.translateService.instant('application.records.error.close-label'),
      3000
    )
  );
  saveChatMessageStatusPopupElement: StatusPopupElement = closePopupData(
    createErrorPopupData(
      this.translateService.instant('application.chat.save-chat-message-error-title'),
      this.translateService.instant('application.chat.save-chat-message-error-message'),
      this.translateService.instant('application.chat.save-chat-message-error-label'),
      3000
    )
  );

  ngOnInit(): void {
    if (this.id) {
      const id = this.id;
      this.messagingService
        .getChatMessages(id)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          switchMap(() => this.messagingService.markMessagesAsRead(id))
        )
        .subscribe(() => {
          this.messagingService.lastNotificationsCount.set(0);
        });
      const webstoreId = this.applicationStore.instant('webstoreData')()?.webstoreId;
      if (webstoreId) {
        this.documentHistory$ = this.documentsService.getDocumentHistory(webstoreId, id).pipe(
          takeUntilDestroyed(this.destroyRef),
          filter(response => !!response),
          tap(response => {
            this.documentsList = this.sortDocumentDates(response);
            for (const document of this.documentsList) {
              this.documentLoadingIds.set(document.externalId, false);
            }
          })
        );
      }
    }
  }

  ngAfterViewChecked(): void {
    if (this.documentsList.length > 2) {
      this.scrollToBottom();
    }
  }

  submit(): void {
    const message = this.chatForm.message().value().trim();
    if (this.id && message) {
      this.messagingService
        .saveChatMessage(this.id, {
          content: message
        })
        .pipe(
          catchError(() => {
            this.setSaveMessageErrorStatusPopup();
            return EMPTY;
          }),
          takeUntilDestroyed(this.destroyRef),
          filter(() => !!this.id),
          switchMap(() => this.messagingService.getChatMessages(this.id ?? ''))
        )
        .subscribe();
      this.chatModel.set({ message: '' });
    }
  }

  downloadFile(doc: DocumentHistoryResponse): void {
    this.documentLoadingIds.set(doc.externalId, true);
    const webstoreId = this.applicationStore.instant('webstoreData')()?.webstoreId;
    if (webstoreId && this.id) {
      this.documentsService
        .getDownloadDocumentById(webstoreId, this.id, doc.externalId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(response => {
            if (response) {
              const fileName = this.getFileName(this.id, doc.documentType);
              arrayBufferConverterToFile(response, fileName);
            } else {
              this.setErrorStatusPopup();
            }
            this.documentLoadingIds.set(doc.externalId, false);
            this.cdr.markForCheck();
          }),
          catchError(err => {
            this.setErrorStatusPopup();
            this.documentLoadingIds.set(doc.externalId, false);
            this.cdr.markForCheck();
            return of(err);
          })
        )
        .subscribe();
    }
  }

  closeStatusMessagePopup(): void {
    this.downloadFileStatusPopupElement = closePopupData(this.downloadFileStatusPopupElement);
  }

  private getFileName(contractId: string | NoValue, documentType: string): string {
    const key = `application.chat.documents.${documentType}`;
    const translatedKey = this.translateService.instant(key);
    const translated = `${translatedKey}.pdf`;
    return contractId ? `${contractId} - ${translated}` : translated;
  }

  private scrollToBottom(): void {
    if (this.documentsContainer()?.nativeElement?.scrollHeight > 0 && !this.scrollHeightInitialized) {
      this.documentsContainer().nativeElement.scrollTop = this.documentsContainer().nativeElement.scrollHeight;
      this.scrollHeightInitialized = true;
    }
  }

  private sortDocumentDates(documents: DocumentHistoryResponse[]): DocumentHistoryResponse[] {
    return documents.sort((a, b) => a.sendDate.getTime() - b.sendDate.getTime());
  }

  private setErrorStatusPopup(): void {
    this.downloadFileStatusPopupElement = createErrorPopupData(
      this.translateService.instant('application.chat.download-error-title'),
      this.translateService.instant('application.chat.download-error-message'),
      this.translateService.instant('application.chat.download-error-close-label'),
      AUTO_CLOSE_DELAY_TEN_SECONDS
    );
    this.cdr.markForCheck();
  }

  private setSaveMessageErrorStatusPopup(): void {
    this.saveChatMessageStatusPopupElement = createErrorPopupData(
      this.translateService.instant('application.chat.save-chat-message-error-title'),
      this.translateService.instant('application.chat.save-chat-message-error-message'),
      this.translateService.instant('application.chat.save-chat-message-error-label'),
      AUTO_CLOSE_DELAY_TEN_SECONDS
    );
    this.cdr.markForCheck();
  }
}
